import { z } from 'zod';
import { type AudiencePlannerResponse } from './aiAudience';
import { type AiEntityList } from './aiEntities';
import { type AnyType } from './any';
import { type CommunicationChannel } from './communications';
import {
    type CustomAttributeType,
    type InsertCustomAttribute,
} from './customAttributes';
import { type UpdateCustomMaterialisation } from './customMaterialisations';
import {
    MetricType,
    type CustomDimension,
    type DimensionType,
    type Schedule,
    type TableCalculation,
} from './field';
import { FilterOperator, UnitOfTime } from './filter';
import { type LearningBlockMapping } from './intelligenceBlock';
import { type JourneyCreatePayload } from './journeys';
import {
    type AdditionalMetric,
    type MetricQuery,
    type NestedMetricQuery,
    type NestedMetricQueryGroup,
} from './metricQuery';
import { type JoinType, type RelationTableType } from './relation/enums';
import type {
    CachedColumnValues,
    CompiledRelation,
    CompiledRelationJoin,
} from './relation/relations';
import { type ChartConfig } from './savedCharts';
import { type TemplateContentDetails } from './templates';
import { type Timezones } from './timezone';

export const USER_THREAD_MAPPING_TABLE_NAME = 'user_thread_mapping';
export const USER_MESSAGE_FEEDBACK_TABLE_NAME = 'user_message_feedback';
export const LANGSMITH_USER_MESSAGE_FEEDBACK_KEY = 'user_message_feedback';

// The use case context in which AI is receiving and generating messages
export enum AIMessageContext {
    SQL_AUDIENCE = 'sql_audience',
    VISUAL_AUDIENCE = 'visual_audience',
    HOME_PAGE = 'home_page',
    EVENT_SETUP = 'events',
    OTHERS = 'others',
    CREATE_CAMPAIGN = 'create_campaign',
    DESIGN_TEMPLATE = 'design_template',
    BUILD_JOURNEY = 'build_journey',
    SET_UP_TASKS = 'set_up_tasks',
    SET_UP_EXPERIMENT = 'set_up_experiment',
    SCHEMA_SEARCH = 'schema_search',
    CHART = 'chart',
    CUSTOM_ATTRIBUTE = 'custom_attribute',
    CUSTOM_ATTRIBUTE_CREATION = 'custom_attribute_creation',
    RELATIONS = 'relations',
    INTERRUPT = 'interrupt',
    ANALYTICS = 'analytics',
}

export type AIContent =
    | TextContent
    | ImageContent
    | AgentJsonContent
    | TableJsonContent
    | AgentErrorContent
    | MemoryContent
    | FeedbackContent;

export type AgentMessage = {
    threadId: string;
    messageId: string;
    author: Author;
    context: AIMessageContext;
    content: AIContent;
    additional_kwargs?: AnyType;
    traceId: string;
    mentionedEntities?: AiEntityList[];
};

export type RelationDetails = {
    relationId: string;
    relation: CompiledRelation;
};

export enum AiMessageTypes {
    TEXT = 'text',
    IMAGE = 'image',
    JSON = 'json',
    TABLE_JSON = 'table_json',
    CHART = 'chart',
    ERROR = 'error',
    MEMORY_EXTRACTED = 'memory_extracted',
    MEMORY_REFERENCED = 'memory_referenced',
    FEEDBACK = 'feedback',
}

export const feedbackRequiredMessageTypes = [
    AiMessageTypes.JSON,
    AiMessageTypes.TABLE_JSON,
];

export type ToolResponse = {
    json?: TableJsonContent | AIChartContent;
    content: string;
};

export type AgentJsonContent = {
    type: AiMessageTypes.JSON;
    value: SQLAudience | VisualAudience | Chart | CustomAttributeAgent;
};

export type TableJsonContent = {
    type: AiMessageTypes.TABLE_JSON;
    value: TableJson[];
};

export type AIChartContent = {
    type: AiMessageTypes.CHART;
    value: Chart;
};

export type AgentErrorContent = {
    type: AiMessageTypes.ERROR;
    value: string;
};

export type TextContent = {
    type: AiMessageTypes.TEXT;
    value: string;
    thinkingText?: string;
};

type ImageContent = {
    type: AiMessageTypes.IMAGE;
    value: string;
    alt?: string;
};

export type ReferencedMemoryWithTitle = {
    content: string;
    timestamp: Date;
    blockId?: string;
    blockTitle?: string;
};

export type MemoryExtractedContent = {
    type: AiMessageTypes.MEMORY_EXTRACTED;
    value: LearningBlockMapping[];
};

export type MemoryReferencedContent = {
    type: AiMessageTypes.MEMORY_REFERENCED;
    value: ReferencedMemoryWithTitle[];
};

export type FeedbackContent = {
    type: AiMessageTypes.FEEDBACK;
    value: string;
};

export type MemoryContent = MemoryExtractedContent | MemoryReferencedContent;

export type VisualAudience = {
    context: AIMessageContext.VISUAL_AUDIENCE;
    nestedMetricQuery: NestedMetricQuery;
};

export type SQLAudience = {
    context: AIMessageContext.SQL_AUDIENCE;
    sql: string;
    explaination: string;
};

export type TableJson = {
    fields: Record<string, AnyType>;
    rows: AnyType[];
    sql?: string;
    title?: string;
};

export type Chart = {
    context: AIMessageContext.CHART;
    metricQuery: MetricQuery;
    chartConfig: ChartConfig;
};

export type CustomAttributeAgent = {
    context:
        | AIMessageContext.CUSTOM_ATTRIBUTE
        | AIMessageContext.CUSTOM_ATTRIBUTE_CREATION;
    customAttribute: InsertCustomAttribute;
};

export type NaturalLanguageSQLResult = {
    question: string;
    response: string | null;
    summary?: string;
};

export enum Author {
    USER = 'user',
    SYSTEM = 'system',
}

export type CsvData = {
    csvOpenAIFileId: string;
    columnNames: string[];
    rowCount: number;
    csvGeneratedFor: string;
};

export type OpenAIObjectIds = {
    assistantId: string;
};

export type FormattedColumn = {
    name: string;
    type: DimensionType;
    description?: string;
    cachedValues?: CachedColumnValues[];
    aiContext?: string;
    synonyms?: string;
};

export type FormattedCustomAttribute = {
    id: string;
    name: string;
    description: string | null;
    sql: string;
};

export type FormattedTable = {
    tableName: string;
    tableLabel: string;
    description: string;
    primaryKey: string;
    type: RelationTableType;
    columns: FormattedColumn[];
    database: string;
    schema: string;
    eventNameColumn?: string;
    eventTimestampColumn?: string;
};

export type FormattedCampaign = {
    id?: string;
    name?: string;
    description?: string;
};

export type FormattedJourney = {
    id: string;
    name: string;
    description?: string;
};

export type RelevantSchemaInfo = {
    tableColumns: FormattedTable[];
    customDimensions: Array<FormattedCustomAttribute>;
    customMetrics: Array<FormattedCustomAttribute>;
    customSqlTraits: Array<FormattedCustomAttribute>;
    joins: CompiledRelationJoin[];
    audiences: AudienceWithSql[];
    campaigns: FormattedCampaign[];
    journeys: FormattedJourney[];
};

export type AgentContext = {
    messageContext: AIMessageContext;
    schemaLinks?: RelevantSchemaInfo;
    projectUuid?: string;
    organisationUuid?: string;
    userUuid?: string;
    customAttribute?: AdditionalMetric | CustomDimension | TableCalculation;
    cookie?: string;
    audiencePlan?: AudiencePlannerResponse;
    csvDataList: CsvData[];
    openAIObjectIds?: OpenAIObjectIds;
    generatedCharts?: GeneratedChart[];
    retryCount: number;
    sqlResult?: {
        sql: string;
        explanation: string;
    };
    metricQuery?: NestedMetricQueryGroup;
    compiledQuery?: string;
    memories?: FormattedMemory[];
    suggestedMemories?: FormattedMemory[];
    currentDate?: string;
    workspaceTimezone?: Timezones;
    customAttributeSql?: string;
    insertCustomAttribute?: InsertCustomAttribute;
    fallbackInsertCustomAttribute?: InsertCustomAttribute;
    customAttributeType?: CustomAttributeType;
    entitySearchQueries?: string[];
    conversationSummary?: ConversationSummary | null;
};

export type FormattedMemory = {
    content: string;
    timestamp: Date;
};

export type GeneratedChart = {
    chartUrl: string;
    timestamp: string;
};

export interface Column {
    name: string;
    type: string;
    description?: string;
    label?: string;
}

export type TableDescription = {
    tableName: string;
    tableDescription: string;
    tableLabel: string;
    runId: string;
};

export type EventColumnMapping = {
    key: string;
    columnName: string;
    columnDataType: string;
    label: string;
    nullable: boolean;
};

export type ColumnDetails = {
    name: string;
    description: string;
    label: string;
    masked?: boolean;
    cached?: boolean;
};

export type RelationsServiceRequest = {
    type: string;
    relationID: string;
    tableID: string;
};

export type TableDetails = {
    type: RelationTableType;
    relationID: string;
    tableID: string;
};

export type EventReservedKeysRequest = {
    user_id: string;
    event_id: string;
    timestamp: string;
};

export type EventExistingColumnsRequest = {
    existingColumns: Record<string, string>;
};

export type JourneyNodeDescriptionRequest = {
    journeyDataSchema: JourneyCreatePayload;
    nodeId: string;
};

export type JourneyNodeDescription = {
    nodeId: string;
    nodeDescription: string;
};

export type SchemaLinkElement = {
    id: string;
    description: string;
    type?: string;
    primaryKey?: string;
};

export type SchemaLinkJoin = {
    join: string;
    joinType: JoinType;
};

export type AdditionaMetricDetails = {
    name: string;
    explanation: string;
    additionalMetric: z.infer<typeof additionalMetricType>[];
};

export type SchemaLinks = {
    columns: Array<SchemaLinkElement>;
    values: Record<string, Array<string>>;
    foreignKeys: Array<SchemaLinkJoin>;
    audiences: Array<SchemaLinkElement>;
    customMetrics: Array<SchemaLinkElement>;
    explanation: string;
};

export type EntityNameDescription = {
    name: string;
    description: string;
    runId: string;
};

export type VisualAudienceDescriptionRequest = {
    context: AIMessageContext.VISUAL_AUDIENCE;
    nestedMetricQuery: NestedMetricQueryGroup;
};

export type SqlAudienceDescriptionRequest = {
    context: AIMessageContext.SQL_AUDIENCE;
    sqlQuery: string;
};

export type CampaignNameDescriptionRequest = {
    audienceIdArray: string[];
    templateId: string;
    channel: CommunicationChannel;
    schedule: Schedule;
};

export type TemplateNameDescriptionRequest = {
    templateContent: TemplateContentDetails;
    channel: CommunicationChannel;
};

export type AudienceDescriptionRequest =
    | VisualAudienceDescriptionRequest
    | SqlAudienceDescriptionRequest;

export type RelationDetailswithCachedValues = {
    relationId: string;
    relation: CompiledRelation;
    allTableCachedValues: Record<string, Record<string, CachedColumnValues[]>>;
};

export type SuggestedPrompt = {
    prompt: string;
    example: string;
};

export type ProfileSuggestions = {
    CustomMaterialisation: UpdateCustomMaterialisation;
    columnsArray: string[];
};

export type UserThreadMapping = {
    threadId: string;
    userUuid: string;
    projectUuid: string;
    createdAt: Date;
    threadName: string;
    updatedAt: Date;
};

export type UserThreadList = {
    data: UserThreadMapping[] | null;
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export type UserMessageFeedback = {
    threadId: string;
    messageId: string;
    sentiment: boolean;
    metadata: Record<string, string>;
    userUuid: string;
    projectUuid: string;
    createdAt: Date;
    traceId: string;
};

export type AIGeneratedNameDescription = {
    name: string;
    description: string;
    runId: string;
};

export type FinalNameDescription = {
    name?: string;
    description?: string;
};

export type DbUserMessageFeedback = {
    message_id: string;
    thread_id: string;
    user_uuid: string;
    project_uuid: string;
    sentiment: boolean;
    metadata: Record<string, string>;
    created_at: Date;
    trace_id: string;
};

export enum FeedbackAuthor {
    AI = 'ai',
    USER = 'user',
}

export enum FeedbackCategory {
    INCOMPLETE_ANSWER = 'incomplete_answer',
    REFERENCED_WRONG_TABLES = 'referenced_wrong_tables',
    FORMATTING_ISSUES = 'formatting_issues',
    INCORRECT_RESULTS = 'incorrect_results',
    SLOW_RESPONSE = 'slow_response',
    UNCLEAR_EXPLANATION = 'unclear_explanation',
    MISSING_CONTEXT = 'missing_context',
    TECHNICAL_ERRORS = 'technical_errors',
    OTHER = 'other',
}

export type GetUserFeedbackResponseDto = {
    status: string;
    results: UserMessageFeedback;
};

export type ConversationSummary = {
    summary: string;
    countMessagesSummarized: number;
};

export const DEFAULT_AUDIENCE_NAME_FORMAT_GUIDELINES = `
    Format: {KeyBehavior} {UserType} {SegmentCharacteristic} {LifecycleStage}
    - Parameters are optional, so omit any that aren't relevant.
    - Keep names concise (under 50 characters), abbreviate if needed.
    - Max 50 characters
    - Use sentence case, not title case (e.g., "Engaged premium onboarding" not "Engaged Premium Onboarding")

    Examples:
    - Engaged premium onboarding
    - Lapsed basic winback
    - Active new users high value retention
    - Engaged enterprise onboardings
    - Active budget
`;

export const DEFAULT_TEMPLATE_NAME_FORMAT_GUIDELINES = `
  Format: {Channel} {Purpose} {KeyTheme} {Action}
  
  - Parameters are optional, so omit any that aren't relevant to your campaign.
  - Keep names concise (under 50 characters), abbreviate if needed.
  - Max 50 characters.
  - Use sentence case, not title case (e.g., "Email retargeting conversion promo" not "Email Retargeting Conversion Promo")

  Examples:
  - Email retargeting conversion promo
  - Push awareness product launch
  - SMS retention brand loyalty reminder
  - InApp engagement upsell
`;

export const DEFAULT_CAMPAIGN_NAME_FORMAT_GUIDELINES = `
  Format: {Channel} {Purpose} {KeyTheme} {Objective}

  - Parameters are optional, so omit any that aren't relevant.
  - Keep names concise (under 50 characters), abbreviate if needed.
  - Max 50 characters.
  - Use sentence case, not title case (e.g., "Email retargeting holiday discount" not "Email Retargeting Holiday Discount")

  Examples:
  - Email retargeting holiday discount
  - Push awareness brand launch golive
  - SMS retention flash sale conversion
  - Social engagement trend campaign upsell
`;

export const additionalMetricType = z.object({
    uuid: z.string(),
    baseDimensionName: z.string(),
    table: z.string(),
    sql: z.string(),
    type: z.nativeEnum(MetricType),
    filters: z
        .array(
            z.object({
                id: z.string(),
                operator: z.nativeEnum(FilterOperator),
                values: z.array(z.string()),
                target: z.object({
                    fieldRef: z.string(),
                }),
                settings: z
                    .object({
                        completed: z.boolean(),
                        unitOfTime: z.nativeEnum(UnitOfTime),
                    })
                    .optional(),
            }),
        )
        .optional(),
    label: z.string(),
    name: z.string(),
    description: z.string(),
});

export type AudienceWithSql = {
    id: string;
    name: string;
    description: string;
    sqlQuery: string;
};

export const DEFAULT_THREAD_NAME = 'New chat';

export type ExtractedMemory = {
    memoryContent: string;
    userFeedback: boolean; // true for positve
};
